import {
  OutputDataObjectGroupProcessingStateType,
  isSafeFiniteInteger,
  useOutputDataGroupProcessingStateRefreshQuery,
  useInterval,
  useRemoveOutputDataAggregateRefreshQueries,
  useSystemClassifierItem,
} from '@atlas-frontend/sdk';
import { useCallback, useMemo } from 'react';

type UseRefreshOutputDataGroupProcessingStatesOptions = {
  isAutoRefreshPaused?: () => boolean;
};

export function useRefreshOutputDataGroupProcessingStates(
  content?: OutputDataObjectGroupProcessingStateType[],
  options?: UseRefreshOutputDataGroupProcessingStatesOptions,
) {
  const { isAutoRefreshPaused } = options ?? {};
  const manual = useSystemClassifierItem('SYSTEM_CONFIGURATION', 'AUTOREFRESH');

  const interval = useMemo(() => {
    if (!isSafeFiniteInteger(manual?.parameters)) {
      return null;
    }

    return Number(manual?.parameters) * 1000;
  }, [manual?.parameters]);

  const ids = useMemo(() => content?.map((value) => value.id) ?? [], [content]);

  const { data: updates, refetch } =
    useOutputDataGroupProcessingStateRefreshQuery(ids, {
      enabled: !!(ids.length && interval),
      initialData: [],
      refetchInterval: false,
    });

  useInterval(
    () => {
      if (!isAutoRefreshPaused?.()) {
        void refetch();
      }
    },
    ids.length ? (interval ?? 0) : 0,
  );

  const removeQueryData = useRemoveOutputDataAggregateRefreshQueries();

  return {
    data: useMemo(() => {
      if (!content?.length || !updates?.length) {
        return content;
      }

      const updateMap = new Map(updates.map((update) => [update.id, update]));

      return content.map((processingState) => {
        const update = updateMap.get(processingState.id);
        if (!update) {
          return processingState;
        }

        return {
          ...processingState,
          ...update,
        };
      });
    }, [content, updates]),
    refresh: useCallback(() => {
      removeQueryData();
      void refetch();
    }, [removeQueryData, refetch]),
  };
}
